import {QuoteDiscountPreview} from "./QuoteDiscounts";
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import {
  activeCcRecipients,
  addPendingCc,
  capCcRecipients,
  caseActiveCcRecipients,
  caseCcLists,
  parseCcInput,
  removePendingCc,
  MAX_CC_RECIPIENTS,
  type CaseCcLists,
  type ThreadCcColumns,
} from "@/lib/ccRecipients";
import { useRealtimeMessages } from "@/lib/useRealtimeCases";
import { supabase } from "@/lib/supabaseClient";
import { isMissingSchemaError } from "@/lib/supabaseErrors";
import { subscribeWithResync, uniqueChannelName } from "@/lib/realtimeResync";
import { Case } from "@/lib/useRealtimeCases";
import { EmailPreview } from "./EmailPreview";
import { AttachmentSelector } from "./AttachmentSelector";
import { AttachmentPreviewModal } from "./AttachmentPreviewModal";
import { useEmailTemplates } from "@/lib/useEmailTemplates";
import { interpolateTemplate } from "@/lib/emailTemplateUtils";
import { detectFileType } from "@/lib/fileTypeUtils";
import type { InboundAttachmentMetadata } from "@/lib/messageMerge";
import { Link } from "react-router-dom";
import { Toast } from "../ui/Toast";
import { useNotifications } from "@/lib/useNotifications";
import { FeedbackInvoiceOption } from '../feedback/FeedbackInvoiceOption';

interface ConversationFeedProps {
  caseId: string;
  /**
   * Fiche courante tenue par l'appelant (CaseDrawer, mise à jour par l'auto-save).
   * Fournie : le fil suit les modifications sans recharger (null = fiche en cours de chargement).
   * Absente : le fil charge lui-même le dossier.
   */
  caseData?: Case | null;
  /** Appelé avant l'envoi (ex. vider l'auto-save du drawer) ; false annule l'envoi. */
  onBeforeSend?: () => Promise<boolean>;
}

/** Copies (Cc) au niveau dossier : union des fils du dossier. */
interface CaseCcState extends CaseCcLists {
  hasThread: boolean;
}

const CC_ERROR_MESSAGES: Record<string, string> = {
  invalid_email: "Adresse e-mail invalide.",
  client_is_primary_recipient: "Le client principal est déjà en « À ».",
  too_many_recipients: `${MAX_CC_RECIPIENTS} copies maximum par dossier.`,
  case_without_thread: "Le fil n’existe pas encore : la copie sera enregistrée au premier envoi.",
  case_not_found: "Dossier introuvable.",
  not_authenticated: "Session expirée, veuillez vous reconnecter.",
};

function ccErrorMessage(error: { message?: string } | null | undefined): string {
  // Migration des copies pas encore appliquée (RPC absente).
  if (isMissingSchemaError(error)) return "La gestion des copies n’est pas encore disponible (mise à jour de la base en attente).";
  const code = Object.keys(CC_ERROR_MESSAGES).find((key) => error?.message?.includes(key));
  return code ? CC_ERROR_MESSAGES[code] : "Impossible de modifier les copies. Réessayez.";
}

async function fetchCaseCcState(caseId: string): Promise<CaseCcState> {
  const full = await supabase
    .from("threads")
    .select("id, participants, manual_participants, excluded_participants")
    .eq("case_id", caseId);
  let rows: ThreadCcColumns[] = (full.data ?? []) as ThreadCcColumns[];
  if (full.error) {
    // Migration des copies pas encore appliquée : lecture seule des participants.
    const legacy = await supabase.from("threads").select("id, participants").eq("case_id", caseId);
    if (legacy.error) throw legacy.error;
    rows = (legacy.data ?? []) as ThreadCcColumns[];
  }
  return { hasThread: rows.length > 0, ...caseCcLists(rows) };
}

interface RecipientsBarProps {
  clientEmail: string | null | undefined;
  loadingCase: boolean;
  cc: string[];
  pending: boolean;
  busy: boolean;
  disabled: boolean;
  input: string;
  error: string | null;
  loadError: boolean;
  onRetryLoad: () => void;
  onInputChange: (value: string) => void;
  onCommit: (raw: string) => void;
  onRemove: (email: string) => void;
}

/** Bandeau « À » (client, fixe) + « Cc » en puces avec saisie en ligne. */
function RecipientsBar({ clientEmail, loadingCase, cc, pending, busy, disabled, input, error, loadError, onRetryLoad, onInputChange, onCommit, onRemove }: RecipientsBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="mb-2 rounded-md border border-gray-200 bg-white text-xs">
      <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-1.5">
        <span className="w-6 shrink-0 font-medium text-gray-500">À</span>
        {clientEmail ? (
          <span className="min-w-0 truncate font-medium text-gray-900" title={clientEmail}>{clientEmail}</span>
        ) : clientEmail === null && loadingCase ? (
          <span className="text-gray-400">Chargement…</span>
        ) : (
          <span className="text-orange-600">⚠️ Email client manquant</span>
        )}
      </div>
      <div
        className="flex cursor-text items-start gap-2 px-2 py-1.5"
        onClick={() => inputRef.current?.focus()}
      >
        <label htmlFor="cc-input" className="w-6 shrink-0 pt-0.5 font-medium text-gray-500">Cc</label>
        <div className="flex max-h-[4.75rem] min-w-0 flex-1 flex-wrap items-center gap-1 overflow-y-auto">
          {cc.map((email) => (
            <span
              key={email}
              className="inline-flex max-w-full items-center gap-0.5 rounded-full bg-indigo-50 py-0.5 pl-2 pr-0.5 text-indigo-800 ring-1 ring-inset ring-indigo-200"
              title={pending ? `${email} — enregistrée au premier envoi` : email}
            >
              <span className="min-w-0 truncate">{email}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(email); }}
                disabled={busy || disabled}
                aria-label={`Retirer ${email} des copies`}
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-indigo-500 hover:bg-indigo-200 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
              >
                ✕
              </button>
            </span>
          ))}
          <input
            id="cc-input"
            ref={inputRef}
            type="email"
            inputMode="email"
            autoComplete="off"
            value={input}
            disabled={busy || disabled}
            onChange={(e) => {
              const value = e.target.value;
              if (/[,;]/.test(value)) onCommit(value);
              else onInputChange(value);
            }}
            onPaste={(e) => {
              const text = e.clipboardData.getData("text");
              if (/[,;\s]/.test(text.trim())) {
                e.preventDefault();
                onCommit(`${input} ${text}`);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (input.trim()) onCommit(input);
              } else if (e.key === "Escape" && input) {
                e.stopPropagation();
                onInputChange("");
              }
            }}
            onBlur={() => { if (input.trim()) onCommit(input); }}
            placeholder={cc.length ? "" : "Ajouter une copie…"}
            aria-describedby={error || loadError ? "cc-error" : "cc-help"}
            aria-invalid={error ? true : undefined}
            className="min-w-[8rem] flex-1 border-0 bg-transparent p-0 py-0.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
        </div>
        {busy && <span className="shrink-0 pt-0.5 text-gray-400" aria-live="polite">…</span>}
      </div>
      {loadError ? (
        <p id="cc-error" role="alert" className="border-t border-red-100 bg-red-50 px-2 py-1 text-red-700">
          Impossible de charger les copies (Cc) du dossier.{" "}
          <button type="button" onClick={onRetryLoad} className="font-medium underline">Réessayer</button>
        </p>
      ) : error ? (
        <p id="cc-error" role="alert" className="border-t border-red-100 bg-red-50 px-2 py-1 text-red-700">{error}</p>
      ) : pending && cc.length > 0 ? (
        <p id="cc-help" className="border-t border-gray-100 px-2 py-1 text-gray-500">Copies enregistrées sur le fil au premier envoi.</p>
      ) : (
        <p id="cc-help" className="sr-only">Entrée ou virgule pour ajouter. Le choix vaut pour toute la suite du fil.</p>
      )}
    </div>
  );
}

export function ConversationFeed({ caseId, caseData: caseDataProp, onBeforeSend }: ConversationFeedProps) {
  const { messages, loading } = useRealtimeMessages(caseId);
  const { notifications, markCaseNotificationsRead } = useNotifications();
  const { templates, loading: templatesLoading } = useEmailTemplates();
  const [newNote, setNewNote] = useState("");
  const [includeFeedback, setIncludeFeedback] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadedCaseData, setLoadedCaseData] = useState<Case | null>(null);
  const caseDataControlled = caseDataProp !== undefined;
  const caseData = caseDataControlled ? caseDataProp : loadedCaseData;
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [showAttachmentSelector, setShowAttachmentSelector] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<Array<{id: string; type: string; file_url: string; label: string}>>([]);
  const [customFiles, setCustomFiles] = useState<Array<{id: string; name: string; type: string; size: number; base64: string}>>([]);
  const [attachmentPreview, setAttachmentPreview] = useState<{ attachments: InboundAttachmentMetadata[]; initialIndex: number } | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "warning"; visible: boolean }>({
    message: "",
    type: "success",
    visible: false,
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Copies (Cc) : persistées sur le(s) fil(s) du dossier, ou en attente tant qu'aucun fil n'existe.
  const [ccState, setCcState] = useState<CaseCcState | null>(null);
  const [pendingCc, setPendingCc] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState("");
  const [ccError, setCcError] = useState<string | null>(null);
  const [ccBusy, setCcBusy] = useState(false);

  const [ccLoadError, setCcLoadError] = useState(false);
  // Miroirs synchrones de l'état des copies : l'envoi lit la dernière valeur même si React
  // n'a pas encore re-rendu (Cc validé au blur juste avant le clic sur Envoyer).
  const ccStateRef = useRef<CaseCcState | null>(null);
  const pendingCcRef = useRef<string[]>([]);
  /** Ajout / retrait de copie en cours : l'envoi l'attend. Résout false si la saisie est refusée. */
  const ccOpRef = useRef<Promise<boolean> | null>(null);

  const applyCcState = useCallback((next: CaseCcState | null | ((prev: CaseCcState | null) => CaseCcState | null)) => {
    const value = typeof next === "function" ? next(ccStateRef.current) : next;
    ccStateRef.current = value;
    setCcState(value);
  }, []);
  const applyPendingCc = useCallback((next: string[] | ((prev: string[]) => string[])) => {
    const value = typeof next === "function" ? next(pendingCcRef.current) : next;
    pendingCcRef.current = value;
    setPendingCc(value);
  }, []);

  const reloadCc = useCallback(async (): Promise<boolean> => {
    try {
      const state = await fetchCaseCcState(caseId);
      applyCcState(state);
      setCcLoadError(false);
      return true;
    } catch (error) {
      console.error("[ConversationFeed] Copies (Cc) non chargées:", error);
      setCcLoadError(true);
      return false;
    }
  }, [caseId, applyCcState]);

  useEffect(() => {
    applyCcState(null);
    applyPendingCc([]);
    ccOpRef.current = null;
    setCcLoadError(false);
    setCcInput("");
    setCcError(null);
    void reloadCc();
    // Une réponse reçue en reply-all met à jour threads.participants : on se resynchronise.
    // Nom unique + resynchronisation (reconnexion, retour sur l'onglet).
    const channel = supabase
      .channel(uniqueChannelName(`threads-cc-${caseId}`))
      .on("postgres_changes", { event: "*", schema: "public", table: "threads", filter: `case_id=eq.${caseId}` }, () => {
        void reloadCc();
      });
    return subscribeWithResync(channel, () => void reloadCc());
  }, [caseId, reloadCc, applyCcState, applyPendingCc]);

  const hasThread = ccState?.hasThread ?? false;
  const ccRecipients = useMemo(
    () => (hasThread && ccState
      ? caseActiveCcRecipients(ccState, caseData?.client_email)
      : activeCcRecipients(pendingCc, [], caseData?.client_email)),
    [hasThread, ccState, pendingCc, caseData?.client_email],
  );

  /** Copies actives d'après les miroirs synchrones (même calcul que `ccRecipients`). */
  const currentCcRecipients = (): string[] => {
    const state = ccStateRef.current;
    return state?.hasThread
      ? caseActiveCcRecipients(state, caseData?.client_email)
      : activeCcRecipients(pendingCcRef.current, [], caseData?.client_email);
  };

  const trackCcOp = (op: Promise<boolean>): Promise<boolean> => {
    ccOpRef.current = op;
    void op.finally(() => {
      if (ccOpRef.current === op) ccOpRef.current = null;
    });
    return op;
  };

  const setCaseCc = async (email: string, active: boolean): Promise<boolean> => {
    const { data, error } = await supabase.rpc("set_case_cc_recipient", {
      p_case_id: caseId,
      p_email: email,
      p_active: active,
    });
    if (error) {
      if (error.message?.includes("case_without_thread")) {
        // Le fil a disparu entre-temps : on repasse en copies en attente.
        applyCcState((prev) => (prev ? { ...prev, hasThread: false } : prev));
        if (active) applyPendingCc((prev) => addPendingCc(prev, email, caseData?.client_email));
        return true;
      }
      setCcError(ccErrorMessage(error));
      return false;
    }
    const result = data as ThreadCcColumns | null;
    applyCcState({ hasThread: true, ...caseCcLists(result ? [result] : []) });
    return true;
  };

  /** Valide la saisie Cc. Résout false si elle est refusée (adresse invalide, limite, erreur serveur). */
  const commitCcInput = async (raw: string): Promise<boolean> => {
    const { valid, invalid } = parseCcInput(raw);
    const client = caseData?.client_email?.trim().toLowerCase();
    const current = currentCcRecipients();
    const toAdd = valid.filter((email) => email !== client && !current.includes(email));
    const problems: string[] = [];
    if (invalid.length) problems.push(`Adresse invalide : ${invalid.join(", ")}`);
    if (client && valid.includes(client)) problems.push(CC_ERROR_MESSAGES.client_is_primary_recipient);
    if (current.length + toAdd.length > MAX_CC_RECIPIENTS) problems.push(CC_ERROR_MESSAGES.too_many_recipients);
    if (problems.length) {
      setCcError(problems.join(" "));
      setCcInput(raw.replace(/[,;]+\s*$/, ""));
      if (invalid.length || current.length + toAdd.length > MAX_CC_RECIPIENTS) return false;
    } else {
      setCcError(null);
    }
    setCcInput("");
    if (!toAdd.length) return true;
    if (!ccStateRef.current?.hasThread) {
      applyPendingCc((prev) => toAdd.reduce((acc, email) => addPendingCc(acc, email, caseData?.client_email), prev));
      return true;
    }
    setCcBusy(true);
    try {
      for (const email of toAdd) {
        if (!(await setCaseCc(email, true))) return false;
      }
      return true;
    } finally {
      setCcBusy(false);
    }
  };

  const removeCc = async (email: string): Promise<boolean> => {
    setCcError(null);
    if (!ccStateRef.current?.hasThread) {
      applyPendingCc((prev) => removePendingCc(prev, email));
      return true;
    }
    setCcBusy(true);
    try {
      return await setCaseCc(email, false);
    } finally {
      setCcBusy(false);
    }
  };

  // Zone de saisie : 3 lignes au départ, grandit avec le contenu jusqu’à ~35 % de la colonne.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const columnHeight = rootRef.current?.clientHeight || window.innerHeight;
    const max = Math.max(96, Math.round(columnHeight * 0.35));
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, max)}px`;
    textarea.style.overflowY = textarea.scrollHeight > max ? "auto" : "hidden";
  }, [newNote, loading]);

  const unreadMessageCount = useMemo(
    () =>
      notifications.filter(
        (notification) =>
          notification.type === "message" &&
          notification.case_id === caseId &&
          !notification.read_at
      ).length,
    [notifications, caseId]
  );

  const hasUnread = unreadMessageCount > 0;

  const markAsRead = async () => {
    await markCaseNotificationsRead(caseId, "message");
  };

  const showToast = (message: string, type: "success" | "error" | "warning" = "success") => {
    setToast({ message, type, visible: true });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  // Charger les données du case pour les templates (seulement si l'appelant ne les fournit pas)
  useEffect(() => {
    if (caseDataControlled) return;
    let cancelled = false;
    setLoadedCaseData(null);
    const fetchCase = async () => {
      const { data } = await supabase
        .from("cases")
        .select("*")
        .eq("id", caseId)
        .single();

      if (data && !cancelled) setLoadedCaseData(data as Case);
    };
    fetchCase();
    return () => { cancelled = true; };
  }, [caseId, caseDataControlled]);

  useEffect(() => {
    // Log minimal en prod pour debug affichage messages
    // eslint-disable-next-line no-console
    console.info("[ConversationFeed] Open case", {
      caseId,
      messagesCount: messages.length,
      loading,
      unreadMessages: unreadMessageCount,
    });
  }, [caseId, messages.length, loading, unreadMessageCount]);

  // Auto-scroll vers le dernier message à l'ouverture
  useEffect(() => {
    if (!loading && messages.length > 0) {
      // Petit délai pour laisser le DOM se stabiliser (ouverture du drawer + rendu)
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const handleSendEmail = async () => {
    if (!newNote.trim() || submitting) return;

    // Les modifications de la fiche (ex. email client) doivent être enregistrées avant l'envoi :
    // le serveur relit le dossier en base.
    if (onBeforeSend && !(await onBeforeSend())) {
      showToast("Les modifications de la fiche n’ont pas pu être enregistrées. Réessayez avant d’envoyer.", "error");
      return;
    }

    if (!caseData?.client_email) {
      showToast("Impossible d'envoyer l'email : l'adresse email du client est manquante.", "error");
      return;
    }

    // Copie saisie juste avant Envoyer : on attend son enregistrement (ou on la valide) pour
    // l'inclure ; si elle est refusée, l'envoi est bloqué et le bandeau Cc dit pourquoi.
    const ccInFlight = ccOpRef.current;
    const ccAccepted = ccInFlight
      ? await ccInFlight
      : ccInput.trim()
        ? await trackCcOp(commitCcInput(ccInput))
        : true;
    if (!ccAccepted) {
      showToast("Envoi bloqué : corrigez ou videz le champ Cc (voir le message au-dessus du texte).", "error");
      return;
    }
    if (!ccStateRef.current && !(await reloadCc())) {
      showToast("Impossible de charger les destinataires en copie : envoi annulé. Réessayez.", "error");
      return;
    }
    const sendCc = currentCcRecipients();
    const threadExists = ccStateRef.current?.hasThread ?? false;

    const { cc: sentCc, dropped: droppedCc } = capCcRecipients(sendCc);
    const ccLine = (sentCc.length ? `\nEn copie (Cc) : ${sentCc.join(", ")}` : "")
      + (droppedCc.length ? `\n⚠️ ${droppedCc.length} copie(s) non envoyée(s) (limite de ${sentCc.length} adresses) : ${droppedCc.join(", ")}` : "");
    const confirmSend = window.confirm(
      `Envoyer cet email à ${caseData.client_email} ?${ccLine}\n\nLe message sera envoyé et enregistré dans la conversation.`
    );

    if (!confirmSend) return;

    setSubmitting(true);
    try {
      // Récupérer le token d'authentification
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Session expirée, veuillez vous reconnecter");
      }

      // Appeler la route API pour envoyer l'email via Resend
      const response = await fetch("/api/mail/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          caseId,
          templateType: null, // Plus de template système hardcodé
          messageText: newNote.trim(),
          attachmentIds: selectedAttachments.map(a => ({ id: a.id, type: a.type })),
          includeFeedback: includeFeedback && caseData.brand === 'smile' && selectedAttachments.some(a=>a.type==='invoice'),
          // Fichiers personnalisés (non stockés, one-shot)
          customAttachments: customFiles.map(f => ({
            filename: f.name,
            content: f.base64,
            contentType: f.type,
          })),
          // Copies choisies avant la création du fil : enregistrées sur le fil au premier envoi.
          ...(!threadExists && sendCc.length ? { pendingCc: sendCc } : {}),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Erreur lors de l'envoi de l'email");
      }

      console.log("✅ Email envoyé avec succès:", result.emailId);

      const sentTo = typeof result.recipient === "string" && result.recipient ? ` à ${result.recipient}` : "";
      if (result.warning) {
        showToast(`Email envoyé${sentTo} ! ${result.warning}`, "warning");
      } else {
        showToast(`Email envoyé${sentTo} !`, "success");
      }

      // L'user a répondu → marquer les notifs messages comme lues
      await markCaseNotificationsRead(caseId, "message");

      // Réinitialiser le formulaire
      setNewNote("");
      setIncludeFeedback(false);
      setSelectedAttachments([]);
      setCustomFiles([]);
      setShowAttachmentSelector(false);
      // Le fil (et ses copies) existe maintenant côté serveur.
      await reloadCc();
      applyPendingCc([]);
    } catch (err: any) {
      console.error("❌ Erreur lors de l'envoi de l'email:", err);
      showToast(`Erreur : ${err.message || "Impossible d'envoyer l'email"}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Fonction pour utiliser un template user
  const handleTemplateClick = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template || !caseData) return;

    const interpolated = interpolateTemplate(template.body, caseData);
    setNewNote(interpolated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        Chargement des messages...
      </div>
    );
  }

  return (
    <div ref={rootRef} className="flex flex-col h-full min-h-0">
      {/* Header avec checkbox "marquer comme lu" */}
      {hasUnread && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <span className="font-medium">💬 Nouveaux messages non lus</span>
          </div>
          <button
            onClick={markAsRead}
            className="text-xs text-red-600 hover:text-red-800 font-medium underline"
          >
            Marquer comme lu
          </button>
        </div>
      )}

      {/* Liste des messages */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            Aucun message pour le moment
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const isClient = msg.sender_type === "client";
              const content = typeof msg.content === "string" ? msg.content : "";
              const senderName =
                typeof msg.sender_name === "string" && msg.sender_name.trim()
                  ? msg.sender_name
                  : isClient
                    ? "Client"
                    : "Staff";
              const attachedFiles = msg.metadata?.attachedFiles ?? [];
              const attachments = msg.metadata?.attachments ?? [];

              return (
                <div
                  key={msg.id}
                  className={`flex ${isClient ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[85%] min-w-0 rounded-lg p-3 ${
                      isClient
                        ? "bg-gray-100 text-gray-900"
                        : "bg-indigo-100 text-indigo-900"
                    }`}
                  >
                    {/* Sender name */}
                    <div className="text-xs font-medium mb-1 opacity-75">
                      {senderName}
                    </div>

                    {/* Content */}
                    <div 
                      className="text-sm break-words whitespace-pre-wrap"
                    >
                      {content}
                    </div>

                    {/* Attachments - Messages sortants (staff) */}
                    {attachedFiles.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200/50 space-y-1">
                        {attachedFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 text-xs opacity-75">
                            <span>{file.type === "quote" ? "📄" : "🧾"}</span>
                            <span className="font-medium">
                              {file.type === "quote" ? "Devis" : "Facture"}:
                            </span>
                            <span className="truncate">{file.name ?? file.label ?? "Fichier joint"}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Attachments - Messages entrants (clients) */}
                    {attachments.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200/50 space-y-1.5">
                        <div className="text-xs font-medium opacity-75 mb-1">
                          📎 {attachments.length} pièce{attachments.length > 1 ? 's' : ''} jointe{attachments.length > 1 ? 's' : ''} :
                        </div>
                        {attachments.map((attachment, idx) => {
                          const { isImage, isPdf, fileIcon } = detectFileType(attachment.filename, attachment.contentType);
                          const fileSizeKB = attachment.size ? Math.round(attachment.size / 1024) : null;
                          const canPreview = isImage || isPdf;

                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <button
                                onClick={() => canPreview && setAttachmentPreview({ 
                                  attachments,
                                  initialIndex: idx 
                                })}
                                disabled={!canPreview}
                                className={`flex items-center gap-2 text-xs p-2 rounded border border-gray-200 transition-colors flex-1 ${
                                  canPreview 
                                    ? 'bg-white/50 hover:bg-white cursor-pointer' 
                                    : 'bg-gray-50 cursor-default'
                                }`}
                              >
                                <span className="text-base">{fileIcon}</span>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="font-medium truncate">{attachment.filename}</div>
                                  {fileSizeKB && (
                                    <div className="text-gray-500 text-xs">{fileSizeKB} Ko</div>
                                  )}
                                </div>
                                {canPreview && <span className="text-gray-400">👁️</span>}
                              </button>
                              <a
                                href={attachment.url}
                                download={attachment.filename}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Télécharger"
                              >
                                📥
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-xs mt-1 opacity-60">
                      {formatDateTime(msg.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Élément invisible pour scroll automatique */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Zone d'ajout de note interne */}
      <div className="shrink-0 border-t border-gray-200 bg-gray-50 p-3">
        <h3 className="sr-only">Écrire un message</h3>

        {/* Destinataires : client en « À », copies en puces */}
        <RecipientsBar
          clientEmail={caseData ? caseData.client_email : null}
          loadingCase={!caseData}
          cc={ccRecipients}
          pending={!hasThread}
          busy={ccBusy}
          disabled={submitting || !ccState}
          input={ccInput}
          error={ccError}
          loadError={ccLoadError}
          onRetryLoad={() => { void reloadCc(); }}
          onInputChange={(value) => { setCcInput(value); if (ccError) setCcError(null); }}
          onCommit={(raw) => { void trackCcOp(commitCcInput(raw)); }}
          onRemove={(email) => { void trackCcOp(removeCc(email)); }}
        />

        {/* Boutons de templates user : une ligne, défilement horizontal si besoin */}
        <div className="mb-2 flex items-center gap-1.5 overflow-x-auto pb-0.5" aria-label="Modèles de message" role="group">
          {templatesLoading ? (
            <div className="text-xs text-gray-500">Chargement des templates...</div>
          ) : templates.length === 0 ? (
            <div className="shrink-0 text-xs text-gray-500">
              Aucun template disponible.{" "}
              <Link to="/templates" className="text-indigo-600 hover:underline">
                Créer mon premier template
              </Link>
            </div>
          ) : (
            templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template.id)}
                className="inline-flex max-w-[11rem] shrink-0 items-center rounded-md px-2 py-1 text-xs font-medium text-white transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
                style={{ backgroundColor: template.color }}
                title={template.name}
              >
                <span className="truncate">{template.name}</span>
              </button>
            ))
          )}

          {/* Bouton pour créer un template */}
          {!templatesLoading && (
            <Link
              to="/templates"
              className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
              title="Créer un nouveau template"
              aria-label="Créer un template"
            >
              ➕{templates.length === 0 ? " Créer un template" : ""}
            </Link>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.ctrlKey) {
              handleSendEmail();
            }
          }}
          aria-label="Message"
          placeholder="Votre message, ou un modèle ci-dessus… (Ctrl+Entrée pour envoyer)"
          className="block w-full resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
          rows={3}
          disabled={submitting}
        />

        {/* Affichage des pièces jointes sélectionnées */}
        {selectedAttachments.some(a=>a.type==='quote') && <QuoteDiscountPreview caseId={caseId} quoteIds={selectedAttachments.filter(a=>a.type==='quote').map(a=>a.id)}/> }
        {import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && caseData?.brand === 'smile' && selectedAttachments.some(a=>a.type==='invoice') && <FeedbackInvoiceOption caseId={caseId} invoiceIds={selectedAttachments.filter(a=>a.type==='invoice').map(a=>a.id)} checked={includeFeedback} onChange={setIncludeFeedback} disabled={submitting}/>}
        {(selectedAttachments.length > 0 || customFiles.length > 0) && (
          <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1.5">
            <div className="mb-1 text-xs font-medium text-indigo-900">
              📎 Pièces jointes ({selectedAttachments.length + customFiles.length}) :
            </div>
            <div className="max-h-20 space-y-0.5 overflow-y-auto">
              {selectedAttachments.map((att) => (
                <div key={att.id} className="flex items-center justify-between gap-2 text-xs text-indigo-700">
                  <span className="min-w-0 truncate" title={att.label}>{att.label}</span>
                  <button
                    onClick={() => setSelectedAttachments(prev => prev.filter(a => a.id !== att.id))}
                    className="shrink-0 text-red-600 hover:text-red-800"
                    aria-label={`Retirer ${att.label}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {customFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between gap-2 text-xs text-amber-700">
                  <span className="min-w-0 truncate" title={file.name}>📁 {file.name}</span>
                  <button
                    onClick={() => setCustomFiles(prev => prev.filter(f => f.id !== file.id))}
                    className="shrink-0 text-red-600 hover:text-red-800"
                    aria-label={`Retirer ${file.name}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <button
            onClick={() => setShowAttachmentSelector(true)}
            disabled={submitting}
            className="flex shrink-0 items-center gap-1 rounded-md border border-indigo-300 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            📎 Joindre ({selectedAttachments.length + customFiles.length})
          </button>
          <button
            onClick={handleSendEmail}
            disabled={!newNote.trim() || submitting || !caseData?.client_email}
            title={ccRecipients.length ? `À : ${caseData?.client_email ?? ""} · Cc : ${ccRecipients.join(", ")}` : undefined}
            className="flex shrink-0 items-center gap-2 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <>
                <span className="animate-spin">⏳</span>
                Envoi en cours...
              </>
            ) : (
              <>
                <span>📤</span>
                Envoyer
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de prévisualisation */}
      {previewHtml && (
        <EmailPreview
          htmlContent={previewHtml}
          onClose={() => setPreviewHtml(null)}
        />
      )}

      {/* Modal de sélection des pièces jointes */}
      {showAttachmentSelector && (
        <AttachmentSelector
          caseId={caseId}
          onClose={() => setShowAttachmentSelector(false)}
          onConfirm={(files, custom) => {
            setSelectedAttachments(files);
            setCustomFiles(custom);
            setShowAttachmentSelector(false);
          }}
        />
      )}

      {/* Modal de prévisualisation des pièces jointes */}
      {attachmentPreview && (
        <AttachmentPreviewModal
          isOpen={true}
          onClose={() => setAttachmentPreview(null)}
          attachments={attachmentPreview.attachments}
          initialIndex={attachmentPreview.initialIndex}
        />
      )}

      {/* Toast notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={hideToast}
      />
    </div>
  );
}
